// packages/ui-gen/src/boilerplate/system-prompt.ts
//
// System-prompt skeleton for the coding agent. Deterministic assembly;
// injection points for funnel content (pitfalls) and for large content
// blocks that are OWNED BY OTHER PACKAGES (design-system docs,
// primitives doc, wire doc). Those blocks travel as strings so ui-gen
// doesn't silently pull in megabytes of auto-generated content from
// `@ggui-ai/design` / `@ggui-ai/wire` that those packages authoritatively
// own.
//
// The only content this skeleton carries by default is the coding-
// criteria summary (`buildCodingCriteriaSummary()` over the open
// `CRITERIA` single-source-of-truth). Everything else defaults to empty
// string — the skeleton still renders cleanly for OSS callers who don't
// pass doc blocks.

import { resolveAppGadgets } from "@ggui-ai/protocol";
import type {
  GadgetDescriptor,
  GadgetExport,
  GadgetHookExport,
  GadgetComponentExport,
} from "@ggui-ai/protocol";
import { buildCodingCriteriaSummary } from "../evaluation/types-public.js";
import {
  extractCallSignaturesFromDts,
  extractComponentPropsFromDts,
} from "../internal/extract-call-signatures.js";

/** Narrow a {@link GadgetExport} to its hook variant by field presence. */
function isHookExport(exp: GadgetExport): exp is GadgetHookExport {
  return "hook" in exp;
}

/** Narrow a {@link GadgetExport} to its component variant by field presence. */
function isComponentExport(exp: GadgetExport): exp is GadgetComponentExport {
  return "component" in exp;
}

export interface SystemPromptInputs {
  /** The user's original request. */
  userRequest: string;
  /** Shell layout mode. */
  shellType?: string;
  /** Target screen size. */
  screen?: string;
  /** Axis-conditioned prompt fragments (from `compose()`). */
  axisDelta?: string;
  /**
   * Funnel content injection — env-gated pitfalls block rendered by the
   * caller. OSS default: `""`. The hosted runtime's core wrapper passes
   * `renderPitfallsBlock()` which honors `GGUI_PITFALLS` /
   * `GGUI_NEW_PITFALLS` env vars.
   */
  pitfallsBlock?: string;
  /**
   * Pre-rendered criteria block. Defaults to
   * `buildCodingCriteriaSummary()` over the open CRITERIA registry.
   * Callers that want to override (e.g. a trimmed summary for fast mode)
   * pass their own string.
   */
  criteriaBlock?: string;
  /** Hand-written design-token reference. Owned by `@ggui-ai/design`. */
  designSystemDocs?: string;
  /** Auto-generated primitives reference. Owned by `@ggui-ai/design`. */
  primitivesDoc?: string;
  /** Auto-generated wire-hooks reference. Owned by `@ggui-ai/wire`. */
  wireDoc?: string;
  /**
   * Per-app gadget catalog. When provided, replaces the default
   * `STDLIB_GADGETS`-only table in the
   * `clientCapabilities — registered catalog` section so registered
   * third-party gadgets (Leaflet, Mapbox, Stripe, …) instruct the
   * code-gen LLM with the same teaching text the synth + decision LLMs
   * see.
   *
   * When omitted, the section renders the standard-library seed (the
   * first-party browser-capability hooks).
   */
  appGadgets?: readonly GadgetDescriptor[];
  /**
   * A `package -> .d.ts content` map for THIRD-PARTY gadget wrappers
   * (the render handler parallel-fetches each non-stdlib gadget's
   * `.d.ts`). When a gadget's `package` has an entry here,
   * `formatGadgetsSection` renders a `**Type**:` line carrying the
   * hook's extracted call signature — the LLM sees the real call shape
   * of a wrapper it cannot otherwise know.
   *
   * Stdlib gadgets (`@ggui-ai/gadgets`) get NO `Type:` line — they
   * already carry an `example` and are well-known; extracting stdlib
   * signatures is deliberately out of scope. Omit for STDLIB-only
   * callers (the section stays byte-identical).
   */
  gadgetTypes?: Readonly<Record<string, string>>;
}

const SHELL_DESCRIPTIONS: Record<string, string> = {
  chat: "inline component inside ChatShell message bubble (~400px wide, compact)",
  fullscreen: "full viewport, responsive layout",
  spatial: "floating AR/VR panel (~600px, touch-friendly)",
};

const SCREEN_DESCRIPTIONS: Record<string, string> = {
  mobile: "single column, large touch targets",
  tablet: "flexible columns, medium spacing",
  desktop: "multi-column, dense layout",
  universal: "responsive across all breakpoints",
};

/**
 * Render the `clientCapabilities — registered catalog` table from the
 * per-app gadget catalog. Each registered gadget renders one row
 * with hook + permission + the `description`-or-`usage` field as the
 * "what it does" column. Permission falls back to `(none)` when the
 * descriptor doesn't declare one.
 *
 * The table is built dynamically from the catalog so all three triad
 * surfaces (synth contract authoring, decision LLM, code-gen system
 * prompt) instruct the model uniformly about which gadgets are
 * available.
 *
 * Rows are emitted in catalog order. An empty catalog produces a hint
 * to seed it via `app.gadgets` rather than an empty table.
 *
 * When `gadgetTypes` carries a `.d.ts` for a gadget's `package`, a
 * `**Type**:` block is appended after the table with one line per
 * THIRD-PARTY gadget: `\`<hook>\`: <extracted call signature>`. The
 * signature is extracted from the wrapper's `.d.ts` via
 * `extractCallSignaturesFromDts`. Stdlib gadgets (`@ggui-ai/gadgets`)
 * get NO `Type:` line — they already carry an `example` and are
 * well-known. A hook whose signature can't be extracted is silently
 * skipped.
 *
 * Pure helper. Exported for unit testing.
 */
export function formatGadgetsSection(
  appGadgets: readonly GadgetDescriptor[],
  gadgetTypes?: Readonly<Record<string, string>>,
): string {
  if (appGadgets.length === 0) {
    return [
      "When the contract declares a `clientCapabilities.gadgets` entry,",
      "the hook MUST be one the operator has registered on",
      "`App.gadgets`. The default ggui server seeds the 7",
      "first-party STDLIB hooks; this server has none registered (the",
      "operator's `ggui.json#app.gadgets` is empty). Don't",
      "declare `clientCapabilities.gadgets` until a hook is registered.",
    ].join(" ");
  }
  // The LLM DIRECT-IMPORTS gadget hooks. The boilerplate emits
  // one combined `import { hookA, hookB } from '<package>'` line per
  // registered gadget package, above a `DO NOT EDIT` banner. The
  // `Package` column below IS the import specifier — it is load-bearing.
  //
  // Each `GadgetDescriptor` is a PACKAGE with `exports[]`;
  // flatten to the hook exports (one table row per hook), carrying
  // package identity through for the Package column.
  const hookExports = appGadgets.flatMap((descriptor) =>
    descriptor.exports
      .filter(isHookExport)
      .map((exp) => ({ exp, descriptor })),
  );
  // Component exports get their own table + render teaching.
  const componentExports = appGadgets.flatMap((descriptor) =>
    descriptor.exports
      .filter(isComponentExport)
      .map((exp) => ({ exp, descriptor })),
  );
  const header =
    "When the contract declares a hook gadget on `clientCapabilities.gadgets`, the hook MUST be one of the registered hooks below. The boilerplate has already emitted a direct import per gadget package — `import { <hook>, … } from '<package>'` — above a `// DO NOT EDIT` banner. KEEP those imports exactly; they are the runtime-resolution anchor and self_check rejects the code if one disappears. Import each STDLIB hook from `@ggui-ai/gadgets`; import each third-party hook from the package named in the `Package` column. DO NOT invent your own import paths. Available registered hooks:";
  const tableHead = [
    "| Hook                  | Package (import from here)         | Permission         | What it does                                |",
    "| --------------------- | ---------------------------------- | ------------------ | ------------------------------------------- |",
  ];
  const rows = hookExports.map(({ exp, descriptor }) => {
    const hookCol = `\`${exp.hook}\``.padEnd(21, " ");
    const pkgCol = `\`${descriptor.package}\``.padEnd(34, " ");
    const permCol = exp.permission
      ? `\`${exp.permission}\``.padEnd(18, " ")
      : "(none)".padEnd(18, " ");
    const what = (exp.usage ?? exp.description ?? "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 80);
    return `| ${hookCol} | ${pkgCol} | ${permCol} | ${what.padEnd(43, " ")} |`;
  });

  // `**Type**:` lines for THIRD-PARTY gadgets. A gadget is third-party
  // when its `package` has a `.d.ts` entry in `gadgetTypes` (stdlib
  // `@ggui-ai/gadgets` is never in the map). The LLM can't know a
  // third-party wrapper's call shape; the extracted signature gives it
  // the exact param/return type to code against.
  // Stdlib gadgets get nothing here by design — `example` covers them.
  const typeLines: string[] = [];
  if (gadgetTypes !== undefined) {
    for (const { exp, descriptor } of hookExports) {
      const dts = gadgetTypes[descriptor.package];
      if (dts === undefined) continue; // stdlib or no `.d.ts` fetched.
      const signatures = extractCallSignaturesFromDts(dts, [exp.hook]);
      const sig = signatures[exp.hook];
      if (sig === undefined) continue; // graceful — couldn't extract.
      typeLines.push(`- \`${exp.hook}\`: \`${sig}\``);
    }
  }
  const typeBlock =
    typeLines.length > 0
      ? [
          "",
          "**Type** (third-party gadgets — call signature from the wrapper's published `.d.ts`):",
          "",
          ...typeLines,
        ]
      : [];

  const hookSection =
    hookExports.length > 0
      ? [header, "", ...tableHead, ...rows, ...typeBlock].join("\n")
      : "";

  // Component gadgets: their own table + RENDER (not call)
  // teaching. A component export is mounted as a JSX element; the LLM
  // never invokes it like a hook.
  const componentRows = componentExports.map(({ exp, descriptor }) => {
    const compCol = `\`${exp.component}\``.padEnd(21, " ");
    const pkgCol = `\`${descriptor.package}\``.padEnd(34, " ");
    const what = (exp.usage ?? exp.description ?? "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 80);
    return `| ${compCol} | ${pkgCol} | ${what.padEnd(53, " ")} |`;
  });

  // `**Props**:` lines for THIRD-PARTY component gadgets. The
  // props-object shape is extracted from the wrapper's published
  // `.d.ts` so the LLM knows the exact JSX attributes of `<X … />`. A
  // component whose `package` has no `.d.ts` entry is skipped
  // (graceful — the `example` still covers it).
  const componentPropsLines: string[] = [];
  if (gadgetTypes !== undefined) {
    for (const { exp, descriptor } of componentExports) {
      const dts = gadgetTypes[descriptor.package];
      if (dts === undefined) continue; // stdlib or no `.d.ts` fetched.
      const propsMap = extractComponentPropsFromDts(dts, [exp.component]);
      const props = propsMap[exp.component];
      if (props === undefined) continue; // graceful — couldn't extract.
      componentPropsLines.push(`- \`${exp.component}\`: \`${props}\``);
    }
  }
  const componentPropsBlock =
    componentPropsLines.length > 0
      ? [
          "",
          "**Props** (third-party component gadgets — prop shape from the wrapper's published `.d.ts`):",
          "",
          ...componentPropsLines,
        ]
      : [];

  const componentSection =
    componentExports.length > 0
      ? [
          "When the contract declares a component gadget on `clientCapabilities.gadgets`, the export is a COMPONENT — RENDER it as a JSX element (`<X … />`) in the tree you return. Do NOT call it like a hook. The boilerplate has already emitted a direct import per gadget package — `import { <Component>, … } from '<package>'` — above a `// DO NOT EDIT` banner. KEEP those imports exactly; they are the runtime-resolution anchor and self_check rejects the code if one disappears. Import each component from the package named in the `Package` column. Available registered components:",
          "",
          "| Component             | Package (import from here)         | What it does                                          |",
          "| --------------------- | ---------------------------------- | ----------------------------------------------------- |",
          ...componentRows,
          ...componentPropsBlock,
        ].join("\n")
      : "";

  return [hookSection, componentSection]
    .filter((section) => section.length > 0)
    .join("\n\n");
}

/**
 * Assemble the coding-agent system prompt. Deterministic — the only
 * variable content comes from the caller-supplied injection fields.
 */
export function buildSystemPrompt(inputs: SystemPromptInputs): string {
  const shell = inputs.shellType ?? "fullscreen";
  const scr = inputs.screen ?? "universal";
  const shellDesc = SHELL_DESCRIPTIONS[shell] ?? SHELL_DESCRIPTIONS.fullscreen;
  const screenDesc = SCREEN_DESCRIPTIONS[scr] ?? SCREEN_DESCRIPTIONS.universal;

  const criteriaBlock = inputs.criteriaBlock ?? buildCodingCriteriaSummary();
  const pitfallsBlock = inputs.pitfallsBlock ?? "";
  const designSystemDocs = inputs.designSystemDocs ?? "";
  const primitivesDoc = inputs.primitivesDoc ?? "";
  const wireDoc = inputs.wireDoc ?? "";
  // Default to the standard-library seed when no per-app catalog is
  // supplied. Callers that resolve `App.gadgets` thread it through here.
  //
  // `gadgetTypes` carries third-party wrapper `.d.ts` content;
  // `formatGadgetsSection` renders a `Type:` line per third-party
  // gadget from it. When omitted, no `Type:` lines are rendered.
  const gadgetsSection = formatGadgetsSection(
    resolveAppGadgets(inputs.appGadgets),
    inputs.gadgetTypes,
  );

  const axisSection =
    inputs.axisDelta && inputs.axisDelta.trim().length > 0
      ? `\n## Shape Guidance\n${inputs.axisDelta}\n`
      : "";

  return `You are ggui's UI builder. You receive a typed boilerplate and fill it in using apply_changes.

## Your Task
${inputs.userRequest}

## Rendering Context
- **Shell**: \`${shell}\` — ${shellDesc}
- **Screen**: \`${scr}\` — ${screenDesc}

## How It Works
1. Read the boilerplate — typed Props, wire hooks, and layout container are pre-configured
2. Respond with one apply_changes call — add state, helpers, and JSX
3. If compilation or evaluation fails, you'll get errors to fix in the next turn

${criteriaBlock}
${axisSection}
## Protocol Notes
The boilerplate pre-declares every wire hook the contract requires (\`useAction\`, \`useStream\`, \`useGguiContext\`, plus capability hooks from \`@ggui-ai/gadgets\` when the contract declares \`clientCapabilities\`). Three rules:
1. **Do NOT delete any pre-declared hook.** \`self_check\` fails with \`wire_preservation:<kind>:<name>\` if you remove one.
2. **Consume every hook binding** somewhere in the component — in JSX, a callback, or an effect. Unused bindings fail lint with \`no-unused-vars\`.
3. **Do NOT invent new wire calls.** Every \`useAction('X')\`, \`useStream('X')\`, \`useGguiContext('X')\` etc. MUST correspond to a declared entry on the contract. Calling one that isn't declared fails \`self_check\` with \`wire_undeclared:<kind>:<name>\` because the runtime has no Context/registration for it and would throw at first paint. If you need a new wire surface, that's a contract authoring step the agent owns — your job is to honor what's declared.

Renaming a binding is fine — the wiring is the string-literal argument, not the identifier.

## Contract surface — four specs + two catalogs

A \`DataContract\` declares everything a GguiSession exchanges with the outside world. **Four typed specs** for the four data-flow directions, **two reference catalogs** for tool / hook lookups:

| Surface              | Direction                  | Role                                                                 |
| -------------------- | -------------------------- | -------------------------------------------------------------------- |
| \`propsSpec\`         | server → UI (one-shot)     | Initial render values delivered once at \`ggui_render\`              |
| \`streamSpec\`        | agent → UI (many)          | Typed channels for live updates via \`ggui_emit\`                    |
| \`actionSpec\`        | UI → agent (events)        | Discrete events driving the agent's next turn (consumed via \`ggui_consume\`) |
| \`contextSpec\`       | UI → server (state mirror) | UI state the agent observes between turns                            |
| \`agentCapabilities.tools\`     | catalog                    | Tools the contract references via \`actionSpec[*].nextStep\` and \`streamSpec[*].source.tool\` |
| \`clientCapabilities.gadgets\` | catalog                    | Browser-capability gadget hooks the component code mounts (e.g., \`useGeolocation\`) |

**Placement rule for inbound specs**: actions drive turns; context observes state. There is no third category.

**Data vs behavior**: the contract describes data flow; the component code describes behavior. Scroll, focus, toast, animation, clipboard write — all component code, never contract fields.

## Defensive coding for absent / late-arriving data

Props arrive at mount and may be partial on first render; later prop changes reach the SAME mounted component via \`ggui_amend\` (in-place repaint) on the live channel. Stream channels start empty and fill over time. Context slots start at their declared default (often \`null\`). **Never assume a field exists before you read it.**

- **Optional contract props**: a \`Props\` field marked \`?\` in the boilerplate may be omitted by the agent ENTIRELY — the wire gate only enforces \`required: true\` entries, and the harness verifies your component with those props stripped. Guard them (\`?? default\`, \`?.\`, or an empty-state branch) from the FIRST render.
- **Array iteration**: always default to \`[]\` before \`.map\`/\`.filter\`/\`.length\`. Use \`(props.items ?? []).map(...)\` not \`props.items.map(...)\`. Same for stream.all, stream.latest, etc.
- **Object access**: optional-chain through nested fields. \`props.user?.name ?? 'Anonymous'\` not \`props.user.name\`.
- **Number ops**: default before arithmetic. \`(props.count ?? 0) + 1\` not \`props.count + 1\`.
- **Stream latest**: \`useStream\` returns \`{latest: T | null, all: T[], isComplete: boolean}\`. The default \`all\` is \`[]\` so it's safe to map; \`latest\` is null until the first frame arrives — guard before reading \`.foo\`.
- **Stream reconciliation**: when a stream event carries an \`action\` discriminant (e.g. \`create | move | edit | delete\`), the channel is a CRUD feed — your handler MUST branch on EVERY value: append on \`create\`, drop on \`delete\`, replace-by-id on \`move\` / \`edit\`. Merging only the "edit" case silently loses created and deleted items. Reconcile into the SAME state that seeds from \`props\` (e.g. \`useState(() => props.tasks ?? [])\`) so the seed data and the live feed render as one list — and handle an event whose id is not yet present (a \`create\` for an unknown item) by inserting it, not ignoring it.
- **Props re-sync on repaint**: props the agent owns are the source of truth for display — prefer rendering DIRECTLY from \`props\` (controlled, no local copy). When local optimistic state genuinely needs a prop seed (a click bumps a count before the agent confirms), it MUST re-sync on prop change — \`useEffect(() => setCount(props.count ?? 0), [props.count])\` — or a \`ggui_amend\` repaint applies the new props while the display keeps the stale local value. BAD: \`const [count, setCount] = useState(props.count ?? 0)\` alone. GOOD: the same seed + the one-line re-sync effect.
- **Loading state**: while data is still absent, render \`<Skeleton>\` placeholders — never a blank screen. \`<Skeleton variant="text" />\` for a text line, \`variant="circle"\` for an avatar slot, default \`rect\` for a block.
- **Empty state**: when a list or results array is empty, render \`<EmptyState title="…" description="…" />\` — a region that renders nothing when empty looks broken to the user.

Unhandled \`Cannot read properties of undefined\` errors trip the iframe error boundary and the user sees "Something went wrong" — a regression class the runtime can't recover from.

## Picking the right primitive for user gestures

Choose by what the user is DOING, not where the result goes — the runtime handles the routing.

| Gesture intent | LLM writes | Notes |
| -------------- | ---------- | ----- |
| Fire a server-side action | \`useAction(name)\` + call \`dispatch(name, payload)\` | Every action is agent-routed. The runtime emits an event on \`ggui_consume\`; the agent reacts on its next turn. If the contract entry declares \`nextStep: 'X'\`, that names the tool the agent SHOULD call next — advisory hint forwarded as event metadata. |
| Surface state to the agent's context | the auto-generated \`setSlotName\` setter (from the boilerplate's \`useGguiContext\` line) | The runtime owns useState + Provider; the boilerplate emits one \`const [slot, setSlot] = useGguiContext<T>('slot')\` line per declared \`contextSpec\` slot. Write plain JSX, no \`useState\`, no Provider wrap. Every value change auto-flows to the host LLM (debounced). One-way client → agent — see "Observable state via \`contextSpec\`" below. |
| Use a browser capability (camera, mic, geolocation, clipboard, file picker, notifications) | call the hook the contract declared, e.g., \`const loc = useGeolocation();\` and \`await loc.start()\` | The contract's \`clientCapabilities.gadgets\` declares which gadget exports the UI uses. The hook implementations live in \`@ggui-ai/gadgets\` (or a third-party package named in the \`Package\` column). Read \`status\` ("idle" / "prompting" / "active" / "completed" / "denied" / "error") to gate UI, and thread the resolved \`value\` into a contextSpec slot or actionSpec payload if the agent needs to see it. |
| Open external link | Plain \`<a href="https://...">\` (or \`target="_blank"\`) | External cross-origin clicks are intercepted and routed through the host (security warnings, app-internal navigation, audit). Same-origin links and \`#fragment\` jumps stay native. |
| Toggle fullscreen / chrome | Plain \`el.requestFullscreen()\` / \`document.exitFullscreen()\` | The native browser API is intercepted; the host adjusts iframe chrome accordingly. Returns a resolved promise so \`.then()\` / \`await\` chains don't break. |

Every gesture fires a uniform server-side audit envelope (\`ggui_runtime_submit_action\`) so operators see all three patterns in RenderInspector with the same shape.

**Don't import wire hooks for link / display-mode.** \`useAction\` is the only wire hook for user gestures; links and fullscreen use plain HTML / browser APIs.

**All actions are agent-routed.** Every action emits an event the agent reacts to on its next turn via \`ggui_consume\`. The optional \`nextStep: '<tool>'\` field on an \`actionSpec\` entry is a HINT naming the tool the agent SHOULD call next — the contract author's recommendation, NOT a binding directive. The agent decides whether to honor it. If you want to declare a tool catalog entry the contract references, add it to \`agentCapabilities.tools[<name>]\` with input/output schemas; the cross-ref linter rejects dangling \`nextStep\` values that don't resolve to a declared catalog entry.

## Making a primitive interactive — \`as={Trait}\`

Structural primitives (\`Box\`, \`Stack\`, \`Row\`, \`Card\`) have NO \`onClick\` by default. Add interactivity with the \`as\` prop — a trait, not a wrapper:

\`\`\`tsx
<Card as={Clickable} onClick={() => dispatch('select', { id })}
      hoverStyle={{ boxShadow: 'var(--ggui-shape-shadow-lg)' }}>…</Card>
\`\`\`

- \`as={Clickable}\` → \`onClick\` + keyboard activation (Enter/Space) + \`role="button"\` + \`hoverStyle\`/\`activeStyle\`/\`cursor\`.
- \`as={Hoverable}\` → \`hoverStyle\` only (no click). \`as={Pressable}\` → \`onPress\` + \`pressStyle\`.

\`as={Trait}\` is a PROP — it does NOT re-nest the JSX. Never write \`<Clickable>…</Clickable>\` around a primitive; put \`as={Clickable}\` on the primitive itself. The trait carries the keyboard + ARIA wiring, so don't hand-write \`onKeyDown\` / \`role\`. Trait components (\`Clickable\`, \`Hoverable\`, \`Pressable\`) import from \`@ggui-ai/design\` like everything else — the boilerplate already imports them.

**Semantic components are already interactive** — \`Button\` (\`onClick\`), \`Link\` (\`href\`), \`Input\` / \`Select\` (\`onChange\`). Use their own props; never put \`as\` on them. \`Text\` picks its element with \`is\` (\`<Text is="label">\`), not \`as\`.

**Never nest two interactive elements.** Interactive content MUST NOT contain other interactive content — a gesture on the inner control bubbles to the outer one and fires BOTH handlers (one user click → the action dispatched twice). Do NOT put a \`Button\`, \`Checkbox\`, \`Input\`, \`Select\`, \`Link\`, or another \`as={Clickable}\` primitive inside a \`Card\` / \`Box\` / \`Row\` / \`Stack\` that is itself \`as={Clickable}\`. Wire each \`useAction\` callback to exactly ONE surface: EITHER the whole card is the trigger (interactive container, no interactive children) OR an inner control is the trigger (plain container, no \`as={Clickable}\`) — never both. A row with a checkbox: put the action on the \`Checkbox onChange\` and leave the row plain.

**\`Text\` / \`Heading\` accept NO event handlers and NO \`as\` — only \`style\` / \`className\` plus their own typed props.** \`onClick\`, \`onDoubleClick\`, \`as={Clickable}\`, \`color\` are all type errors on \`Text\`. When the request says a label is "clickable", "editable", "edit on click / double-click", or "tap to …", do ONE of these — never put the handler on \`Text\`:

\`\`\`tsx
// Click-to-edit a label: wrap the Text in a Clickable structural primitive.
<Box as={Clickable} onClick={() => setEditingId(task.id)}
     style={{ cursor: 'pointer' }}>
  <Text weight="semibold">{task.title}</Text>
</Box>

// Or pair the label with an explicit edit Button (clearer affordance).
<Row gap="xs" align="center">
  <Text weight="semibold">{task.title}</Text>
  <Button variant="ghost" size="xs" aria-label="Edit title"
          onClick={() => setEditingId(task.id)}>Edit</Button>
</Row>

// In edit mode, swap the Text for an Input.
{editingId === task.id
  ? <Input value={draftTitle} onChange={setDraftTitle} label="Task title" />
  : <Text weight="semibold">{task.title}</Text>}
\`\`\`

## Anti-patterns — DO NOT WRITE

The following identifiers / shapes are RETIRED from the contract surface as of 2026-05-11. Pre-2026-05-11 examples in your training data may include them; do not reproduce. The linter / CI grep gate rejects:

- \`useWiredTool\`, \`useClientTool\` — retired hooks. Replace with \`useAction\` (events) and the named hook from \`@ggui-ai/gadgets\` (browser capabilities).
- \`dispatch: { kind: 'tool', tool: '...' }\` / \`dispatch: { kind: 'agent', intendedTool: '...' }\` — retired discriminated-union. Use the flat optional \`nextStep?: '<tool>'\` instead.
- \`mode: 'host-routed'\` / \`mode: 'tool'\` — retired \`mode\` field. Same fix: flat \`nextStep?\`.
- \`broadcast: {...}\` on the contract — retired top-level field. Use \`streamSpec[channel].source: {tool, args?}\` to declare a tool-fed channel.
- \`wiredTools\` / \`agentTools\` (top-level) — retired catalog names. Use \`agentCapabilities.tools\`.
- \`clientTools\` / \`clientCapabilities.capabilities\` — retired catalog shapes. Use \`clientCapabilities.gadgets\` (entries declare hooks, not RPC).
- \`@ggui-ai/client-tools\` — retired package name. Import gadget hooks from \`@ggui-ai/gadgets\`.
- \`intendedTool\` — retired. Use \`nextStep\` (flat).
- \`props: { properties: {...} }\` as a CONTRACT field — retired. The contract field is \`propsSpec\` (the wire \`props\` field on render / update still carries VALUES).

## Cross-reference rules

When you declare a reference, also declare the catalog entry it points at:

- \`actionSpec[X].nextStep = 'fetch_inbox'\` → \`agentCapabilities.tools.fetch_inbox = { toolInfo: { inputSchema, description?, outputSchema? }, usage?, example? }\` MUST exist. Cross-ref code: \`CTR_REF_NEXT_STEP\`.
- \`streamSpec[X].source.tool = 'list_messages'\` → \`agentCapabilities.tools.list_messages\` MUST exist. Cross-ref code: \`CTR_REF_STREAM_SOURCE\`.
- The catalog entry's schemas MUST be a superset of the referencing spec's schema. Cross-ref code: \`CTR_SCHEMA_INCOMPAT\`.

## clientCapabilities — registered catalog

${gadgetsSection}

Each hook conforms to \`GadgetHook<TOutput, TOptions>\`: call \`start(opts?)\` to fire, read \`{value, status, error, stop?}\`. \`status\` walks through \`idle → prompting → active|completed\` or routes to \`denied\` / \`error\` on failure.

3rd-party plugins (Leaflet maps, Mapbox, Stripe, Chart.js, …) are registered via \`createGguiGadget\` from \`@ggui-ai/gadgets\` and surface in this same table when the operator has added them to \`App.gadgets\`. Reference any registered hook by name — render validation rejects hooks not in this catalog with \`gadget_not_registered\`.

## Observable state via \`contextSpec\`

When the contract declares \`contextSpec\`, the boilerplate auto-generates one \`useGguiContext\` call per slot at the top of your component. The runtime owns the underlying \`useState\` and the Provider tree — **you do NOT write \`useState\` or any \`<Provider>\` wrap yourself**:

\`\`\`tsx
import { useGguiContext } from '@ggui-ai/wire';

export default function Component(props: Props) {
  // AUTO-GENERATED — do not remove or rename:
  const [currentStep, setCurrentStep] = useGguiContext<number>('currentStep');
  const [draftText, setDraftText] = useGguiContext<string>('draftText');

  // Plain JSX. No Provider wrap. The runtime already wrapped your
  // component in nested SingleSlotProviders before this code ran.
  return (
    <Container>
      <Text>Step {currentStep}</Text>
      <Input value={draftText} onChange={(e) => setDraftText(e.target.value)} />
      <Button onClick={() => setCurrentStep((s) => s + 1)}>Next</Button>
    </Container>
  );
}
\`\`\`

For every declared slot you have **\`slotName\` + \`setSlotName\`** in scope:
- **Read** the value to render: \`<Text>Step {currentStep}</Text>\`
- **Write** via the setter: \`setCurrentStep(s => s + 1)\` (in callbacks, effects, anywhere)

Every value change is mirrored to the host LLM's context automatically (debounced, default 300ms — adjustable per-slot via \`entry.debounceMs\` in the contract). The agent sees the user's interaction state — drafts, current step, hover, selection — without you calling any API.

**When to use the auto-generated state.** Any slot the contract declared. If \`contextSpec.draftText\` exists, bind \`<Input value={draftText} onChange={e => setDraftText(e.target.value)}>\` so the agent sees the typing live. If \`contextSpec.currentStep\` exists, render the step indicator from \`currentStep\` and bump it via \`setCurrentStep\` in your "next" callback.

**When NOT to use it.** Local UI state the contract did NOT declare — \`isDropdownOpen\`, hover flags, animation phase, ephemeral toggles. For those, use a plain \`useState\` directly. The runtime ignores undeclared state.

**\`contextSpec\` direction is one-way: client → agent.** The agent uses \`propsSpec\` (via \`ggui_amend\` for in-place repaints of this mount, or \`ggui_update\` when it mints a new history card) and \`streamSpec\` (via the live channel) to push state TO the client. Don't try to write to the agent via \`contextSpec\` — there is no return path.

**Schema mismatches drop silently.** If you set a value that doesn't match the slot's schema (e.g. a string into a \`{type: 'number'}\` slot), the runtime logs a dev \`console.warn\` and skips the post. Make sure your setter calls produce values that match the declared shape.

${pitfallsBlock}

## Reference: Wire Hooks
${wireDoc}

${DESIGN_SYSTEM_GUIDANCE}

### CSS Token Documentation
${designSystemDocs}

### Component Reference
${primitivesDoc}
`;
}

/**
 * Hand-written design-system guidance embedded in every coding-agent
 * system prompt. Lists prop-value enums, branded color strategy, and
 * responsive/parameterization rules that make a generated component
 * feel polished — the system prompt without it would only list
 * primitive *names*. Exported so `prompt-type-drift.test.ts` can
 * verify its enum claims against the auto-generated primitive catalog.
 */
export const DESIGN_SYSTEM_GUIDANCE = `## Imports & Component Surface

Import ONLY from: \`react\`, \`@ggui-ai/design\`, \`@ggui-ai/wire\`. The ENTIRE design system — every primitive, component, composition and trait — is exported from the single \`@ggui-ai/design\` entry: \`import { Card, Grid, Stack, Modal, Clickable } from '@ggui-ai/design'\`. There are NO subpaths (\`/primitives\`, \`/components\`, …) — never import from them. Use the design components — DO NOT use raw HTML elements (\`<button>\`, \`<input>\`, \`<div>\` for layout) or Tailwind classes; those render unstyled in the iframe runtime.

Available primitives (all from \`@ggui-ai/design\`):
- Layout: Box, Container, Stack, Row, Grid, Spacer, Divider
- Typography: Heading, Text, Link
- Form: Button, Input, TextArea, Checkbox, Toggle, RadioGroup, Select, Slider
- Display: Card, Alert, Badge, Avatar, Image, Icon, Progress, Spinner, Skeleton, Tooltip
- Composite: Accordion, Tabs, Table, Toast

Available compound components (all from \`@ggui-ai/design\`):
- Autocomplete, Breadcrumb, Dropdown, EmptyState, FormField, Markdown, MarkdownInline, MenuItem, Pagination, SearchField, Stat, Stepper, Tag

**Choosing between similar components** — pick by intent, don't guess:
- **Pick from options**: one value from a short fixed list (a form field) → \`Select\`. Type-to-filter a long list, then pick → \`Autocomplete\`. A menu of actions off a button (edit / delete / …) → \`Dropdown\`. A search box that filters displayed content → \`SearchField\`.
- **Tabular data** → \`Table\`. Reach for \`DataTable\` ONLY when you need built-in sorting / pagination / row-selection.
- **Messaging**: an inline message in the layout flow → \`Alert\`. A transient popup → \`Toast\`. A panel listing many notifications → \`NotificationCenter\`.
- **Step progress**: a wizard's step indicator → \`Stepper\`. NOT \`Tabs\` (implies free navigation), NOT \`Progress\` (continuous bar), NOT \`Breadcrumb\` (hierarchy).
- **Containers**: width-constrain a page region → \`Container\`. A visually-contained surface (background + shadow + border) → \`Card\`. Plain grouping / spacing with no chrome → \`Box\`.

EXACT primitive prop values (other values are silently ignored — the design system maps them to defaults):
- \`<Text size="...">\` — ONLY \`xs | sm | base | lg | xl | 2xl | 3xl | 4xl\`. For a HUGE number/temperature, use \`<Text size="4xl" weight="bold">\`. \`caps\` uppercases with letter-spacing — the eyebrow/overline label look is \`<Text size="xs" weight="semibold" caps tone="muted">\`.
- \`<Text weight="...">\` — \`normal | medium | semibold | bold\`.
- \`<Text tone="...">\` — typed semantic slot. \`default | muted | subtle | emphasized | loud | success | warning | error | info | inverse | inherit\`. The theme decides what each tone LOOKS like — \`muted\` is a quiet warm grey on Claudic, a cool slate on Indigo. \`tone\` is the ONLY way to set Text color; the legacy \`color="..."\` prop has been removed.
- \`<Heading level={1|2|3|4|5|6}>\` — sizes are preset by level (h1 = 4xl bold, h2 = 3xl bold, h3 = 2xl semibold). Pass a number, not \`level="h1"\`. Heading uses the same \`tone\` slot vocabulary as Text.
- \`<Icon name="..." tone="...">\` / \`<Spinner tone="...">\` / \`<Link href="..." tone="...">\` / \`<Divider tone="...">\` — same \`tone\` vocabulary as Text. Default = \`currentColor\` (Icon), primary-tinted (Spinner / Link), outlineVariant (Divider). Use \`tone="inherit"\` when you want the element to track the parent's foreground color (e.g. an Icon next to muted text).
- \`<Button variant="...">\` — \`primary | secondary | outline | ghost | danger\`. Sizes \`xs | sm | md | lg\`. Use \`primary\` for the main action — renders in the brand color automatically.
- \`<Card padding="lg" shadow="md" radius="lg" surface="default">\` — shadow \`none|sm|md|lg|xl\`, radius \`none|sm|md|lg|xl\`. \`surface\` slot picks the fill: \`default | elevated | sunken | accent | inverted | transparent\`. Use \`inverted\` for dark testimonial-style cards on a light theme; \`accent\` for branded fills.
- \`<Box surface="...">\` — same surface slots as Card. \`surface\` is the ONLY theme-tracking background prop; the legacy \`background="..."\` prop has been removed. For non-theme-mapped brand colors (a partner's exact brand hex like Stripe purple), use the typed escape \`<Box assetColor="#635BFF" assetSemantic="stripe-brand-purple">\` — both props are required, and \`assetSemantic\` MUST be a non-empty human-readable label. Tier-0 self-check rejects every other hex / rgba on Box.
- \`<Stack gap="...">\` / \`<Row gap="...">\` — \`gap\` takes the **spacing scale** (next bullet). \`align\` (cross-axis) is ONLY \`start | center | end | stretch\` and \`justify\` (main-axis) is ONLY \`start | center | end | between | around | evenly\` — NEVER the raw CSS values \`flex-start\` / \`flex-end\` / \`space-between\`, which are type errors.
- **Spacing scale** — \`gap\` (Stack / Row / Grid) and \`padding\` (Card / Box / Container) take a t-shirt size: \`none | xs | sm | md | lg | xl | 2xl\`. Each resolves to a \`--ggui-spacing-*\` token (xs≈4px, sm≈8px, md≈16px, lg≈24px, xl≈32px, 2xl≈48px). A bare number is treated as pixels. NEVER pass a raw CSS length such as \`gap="8px"\` — it is silently dropped by the browser and the gap collapses to 0; use the scale name (\`gap="sm"\`).
- \`<Grid columns={N} gap="md">\` — 2-D layout (rows AND columns). Reach for it for card galleries, stat grids and dashboards — NEVER hand-roll \`style={{ display: 'grid' }}\`. When the request names exact per-breakpoint counts ("3 per row on desktop, 1 on mobile"), pass a map: \`<Grid columns={{ base: 1, md: 3 }}>\` (breakpoints \`sm\`/\`md\`/\`lg\`/\`xl\`; the design system emits the media queries). For an open-ended gallery where any column count is fine, use \`<Grid minColumnWidth={220}>\` — it fits as many equal columns as the width allows. \`radius\` (Card / Box / Image) takes the scale \`none | sm | md | lg | xl\`.
- \`<Stat label="…" value="…" delta="+12%" trend="up">\` — KPI display (label + big value + trend-coloured delta + optional \`icon\`). \`trend\` is \`up | down | neutral\` (delta renders green / red / muted). Reach for it for any "show a number" UI; drop several into a \`<Grid>\` for a stat grid instead of hand-building label+value pairs.
- \`<Stepper steps={STEPS} current={step} />\` — display-only step indicator for wizards/checkouts. \`steps\` is a top-level \`const\` array of labels; \`current\` is YOUR zero-indexed \`useState\` value; \`orientation\` is \`horizontal | vertical\`; optional \`onStepClick={(i) => …}\` makes steps clickable. Stepper never owns navigation state — your Next/Back handlers move \`current\`.
- \`<Badge variant="...">\` — \`default | primary | secondary | success | warning | error | info\` for colored pills. Great for status/condition labels. There is NO \`neutral\` variant — use \`default\` (or \`secondary\`) for an un-tinted pill.

**Color choice rule of thumb.** Reach for typed slots first: Button \`variant\`, Badge \`variant\`, Alert \`variant\`, Text/Heading/Icon/Spinner/Link/Divider \`tone\`, Box/Card \`surface\`. NEVER hardcode hex \`#XXXXXX\`, rgba, or hsl — tier-0 self-check rejects them with \`tokens:hex-color\` / \`tokens:hardcoded-color-fn\` and the LLM must remediate. Hardcoded colors break the operator's theme switch (Indigo → Claudic → Cyberpunk preset has zero effect on a card hardcoded with \`background: '#000'\`).

**Asset-color escape (Box only).** When you genuinely need a non-theme color — a partner's exact brand hex (Stripe purple \`#635BFF\`, Slack aubergine \`#4A154B\`), a fixed product surface — use \`<Box assetColor="#635BFF" assetSemantic="stripe-brand-purple">…</Box>\`. The \`assetSemantic\` is REQUIRED and MUST be a non-empty human-readable label that documents intent. Tier-0 allows hex inside this typed pair; one without the other fails the check. Reach for \`surface\` first — \`assetColor\` is rare.

## Accessibility (REQUIRED)

The design-system primitives are accessible by construction — they emit their own roles, labels, keyboard handlers, and error wiring. Your job is to USE them correctly, NOT to re-declare ARIA on top of them.

1. **Form inputs** — give every \`Input\` / \`TextArea\` / \`Select\` a \`label\` prop. The primitive renders its own \`<label htmlFor>\`, and exposes \`aria-invalid\` + \`aria-describedby\` for errors. Do NOT add a separate \`<Text>\` label or your own \`htmlFor\` — that double-labels the field.
   \`\`\`tsx
   <Input label="Email" value={email} onChange={setEmail} type="email" />
   \`\`\`
2. **Don't re-declare built-in ARIA.** \`Progress\`, \`RadioGroup\`, \`Tabs\`, \`Toggle\`, \`Slider\`, \`Spinner\`, \`Alert\`, \`Accordion\` already carry the correct \`role\` / \`aria-*\`. \`Card as={Clickable}\` already adds \`role="button"\` + keyboard activation. Adding your own is redundant and often wrong.
3. **Icons are decorative by default** — \`<Icon name="check" />\` is hidden from screen readers, which is correct for an icon sitting next to text. Add \`aria-label\` ONLY for a standalone, meaning-bearing icon with no adjacent text. Icon-only \`Button\`s still need \`aria-label\` on the **Button** itself.
4. **Live & streaming data** — wrap any region whose content updates on its own (a \`useStream\` \`.latest\` value, a live clock, an "N new" counter, a flashing price) in an element with \`aria-live="polite"\` so screen readers announce the change.
5. **Headings nest** — one \`<Heading level={1}>\` per screen, \`level={2}\` for sections, \`level={3}\` for subsections. Never skip or invert levels.
6. **Buttons** — descriptive text content; icon-only buttons need \`aria-label\`. Announce busy state: \`<Button disabled={isLoading} aria-busy={isLoading}>{isLoading ? 'Submitting…' : 'Submit'}</Button>\`.
7. **Stateful controls announce their state.** Toggleable / selectable / expandable things use the primitive that carries the semantics (\`Checkbox\`, \`Toggle\`, \`Tabs\`, \`Accordion\`) — never a styled \`Box\`/\`Icon\` that only *looks* checked. When you hand-roll one with \`as={Clickable}\`, \`Clickable\` adds button + keyboard semantics but NOT state — add it yourself: \`role="checkbox"\` + \`aria-checked={done}\` on a toggleable row, \`aria-pressed\` on a toggle button, \`aria-selected\` on the chosen item, \`aria-expanded\` on an open/close affordance. Styling (strikethrough, color, a check icon) must never be the only carrier of state.

## Design System Usage (CRITICAL)

EVERY color, spacing, typography, shadow, and radius value MUST come from design-system CSS variables. The runtime injects them on \`:root\`.

MANDATORY:
1. NEVER use hardcoded hex colors like \`#7c3aed\` — ONLY \`var(--ggui-color-*)\` tokens.
2. NEVER use CSS gradients with custom colors. If you need a gradient: \`linear-gradient(to bottom, var(--ggui-color-primary-500), var(--ggui-color-primary-700))\`.
3. NEVER invent your own palette. The system provides primary, neutral, success, warning, error, and info — use ONLY these.
4. NEVER include literal fallback values in color tokens — write \`var(--ggui-color-primary-600)\` bare, never \`var(--ggui-color-primary-600, #0284c7)\`. The runtime injects every token; a literal fallback paints the wrong brand exactly when the operator's theme matters most.

Token categories:
- Brand: \`var(--ggui-color-primary-600)\`, \`var(--ggui-color-primary-50)\`
- Text: \`var(--ggui-color-onSurface)\`, \`var(--ggui-color-onSurfaceVariant)\`
- Backgrounds: \`var(--ggui-color-surface)\`, \`var(--ggui-color-surfaceVariant)\`
- Borders: \`var(--ggui-color-outline)\`
- Spacing: \`var(--ggui-spacing-4, 16px)\`, \`var(--ggui-spacing-6, 24px)\`
- Typography: \`var(--ggui-font-size-sm, 14px)\`, \`var(--ggui-font-weight-semibold, 600)\`
- Shadows: \`var(--ggui-shape-shadow-sm)\`, \`var(--ggui-shape-shadow-md)\`, \`var(--ggui-shape-shadow-lg)\`
- Radius: \`var(--ggui-shape-radius-md, 8px)\`, \`var(--ggui-shape-radius-lg, 12px)\`

Prefer primitives' built-in styling props over inline styles when possible.

### Branded Color Strategy

Use the FULL primary palette throughout the component — NOT only on submit buttons. A well-themed component feels distinctly branded, not gray-with-one-colored-button.

| Element | Token | Purpose |
|---------|-------|---------|
| Section headers, hero areas, highlight strips | \`primary-50\` / \`primary-100\` | Subtle branded backgrounds |
| Borders, dividers, focus rings, input focus | \`primary-200\` / \`primary-300\` | Branded structure |
| Icons, links, labels, active indicators | \`primary-500\` / \`primary-600\` | Core accent color |
| Buttons, CTAs, filled interactive elements | \`primary-600\` / \`primary-700\` | Primary actions |
| Headings on light primary backgrounds | \`primary-800\` / \`primary-900\` | High-contrast branded text |

Use semantic tokens (\`onSurface\`, \`onSurfaceVariant\`) for body text and secondary info. NEVER use raw \`neutral-*\` or \`gray-*\` for body text — they break in dark themes.

### Theme-Agnostic Design

Components MUST be theme-agnostic — they reference CSS variables but NEVER assume a specific style. The theme decides what \`primary-600\` looks like.

DO:
- Use \`var(--ggui-color-primary-*)\` for brand elements — the theme controls what "primary" means
- Use \`var(--ggui-shape-shadow-*)\` for depth, \`var(--ggui-shape-radius-*)\` for corners
- Use semantic color roles: primary for brand, surface/onSurface for structure, success/error/warning for state

DON'T:
- Don't assume primary is blue — could be red, green, purple
- Don't hardcode gradients tuned for a specific theme
- Don't use fixed shadow values

Visual hierarchy via tokens:
- Elevated sections: \`var(--ggui-shape-shadow-md)\` + \`var(--ggui-shape-radius-lg)\`
- Highlighted regions: \`var(--ggui-color-primary-50)\` background
- Active/selected: \`var(--ggui-color-primary-100)\` background
- Section headers: \`var(--ggui-color-primary-600)\` text or border-bottom

## Responsive Design (CRITICAL)

Generated components become reusable blueprints — the same blueprint serves phones, tablets, desktops, spatial headsets. Design for ALL screen sizes:

1. Design tokens for ALL spacing — never hardcode pixel values for padding/margins/gaps. Use the named spacing scale on props (\`gap="md"\`, \`padding="lg"\`); for inline \`style\` use \`var(--ggui-spacing-*, …)\`.
2. Relative/fluid units — prefer \`%\`, \`em\`, \`rem\`, \`min()\`, \`max()\`, \`clamp()\` over fixed \`px\`.
3. Fluid widths — \`max-width\` with \`width: 100%\`. Never set a fixed width.
4. Compact padding — components are embedded in containers that provide their own chrome.
5. No raw \`@media\` queries in component code — for a layout that must change by breakpoint, use \`<Grid columns={{ base: 1, md: 3 }}>\` (the design system emits the media queries for you) or a fluid \`minColumnWidth\` grid.

## Data Parameterization (CRITICAL)

Generated components are CACHED blueprints reused across requests. NEVER hardcode request-specific data (names, cities, numbers, dates) into the component body. Define data as default prop values so the blueprint works for ANY similar request:

\`\`\`tsx
// BAD — hardcoded, only works for Tokyo
const city = "Tokyo";
const temp = 18;

// GOOD — parameterized via props with defaults from the request
interface Props {
  city?: string;
  temperature?: number;
}
export default function WeatherCard({ city = "Tokyo", temperature = 18 }: Props) {
  // A controller can override for Seoul, Paris, etc.
}
\`\`\`

Rules:
1. All request-specific data → props with defaults. City names, tickers, user names, dates, counts.
2. Layout and styling are universal. Colors, spacing, structure — these are the reusable part.
3. Default values come from the current request — so the component renders correctly standalone.
4. Props interface must be typed and exported.

## Component Structure

Keep JSX nesting depth to 3–5 levels. When deeper, extract repeated/complex sections into helper components — named functions defined above the main Component in the same file. Helpers take data + callbacks via props; they don't own state.

\`\`\`tsx
import { useState } from 'react';
import { Container, Card, Stack, Text, Button, Input } from '@ggui-ai/design';

interface Props {
  onSubmit?: (data: unknown) => void;
}

function ItemCard({ item, onEdit }: { item: Item; onEdit: (id: string) => void }) {
  return <Card padding="md">…</Card>;
}

export default function GeneratedComponent({ onSubmit }: Props) {
  return (
    <Container>
      {items.map((item) => <ItemCard key={item.id} item={item} onEdit={handleEdit} />)}
    </Container>
  );
}
\`\`\`

## Aesthetic Guidance (READ CAREFULLY — this is what separates "polished" from "ok")

### Visual hierarchy — the SCALE GAP rule

A polished UI has ONE hero that dominates. Everything else supports it. Bad layouts have everything at similar sizes — the eye has nowhere to land. The rule:

**Hero metric vs supporting text must have a 2–3× size gap.** If the hero is the temperature, score, count, status, price — it's ENORMOUS. Use \`<Text size="4xl" weight="bold">\` — the largest \`size\` the type allows (\`4xl\` = 36px). Pair it with a small supporting label (\`size="sm"\`, ~14px) so the gap reads as 2–3×. The hero number should feel oversized compared to the location/title around it. \`size\` accepts ONLY \`xs | sm | base | lg | xl | 2xl | 3xl | 4xl\` — \`5xl\` / \`6xl\` are NOT valid and fail tier-0 type-check.

\`\`\`tsx
// BAD — temperature is the same size as the location heading
<Heading level={1}>Seoul, South Korea</Heading>
<Text size="lg" weight="bold">18°C</Text>

// GOOD — temperature dominates (4xl), location supports it (sm)
<Text size="sm" tone="muted">Seoul, South Korea</Text>
<Text size="4xl" weight="bold">18°C</Text>
<Text size="lg" tone="muted">Partly Cloudy · Feels like 16°C</Text>
\`\`\`

### Color discipline — the 60/30/10 rule

Don't paint everything in primary. Use:
- **60% surface** (\`var(--ggui-color-surface)\` / \`onSurface\`) — body text, default backgrounds, structure
- **30% surfaceVariant + onSurfaceVariant** — secondary text, captions, labels, dividers
- **10% primary** — hero number, ONE highlight element, CTAs, brand accent

If your component is 100% purple text on purple backgrounds, you've lost the eye. Headings can be \`onSurface\` (dark neutral) — they'll still feel weighty. Save the primary palette for one or two STAR moments.

\`\`\`tsx
// BAD — everything purple, eye has no anchor
<Heading tone="emphasized">Title</Heading>
<Text tone="emphasized">42</Text>
<Text tone="emphasized">all body text</Text>

// GOOD — hero pops, body is neutral, primary is reserved
<Heading>Title</Heading>  {/* defaults to onSurface */}
<Text size="4xl" weight="bold" tone="emphasized">42</Text>
<Text tone="muted">all body text</Text>
\`\`\`

### Visual rhythm — vary your card treatments

A row of identical flat tiles feels monotone. Use card-treatment variation to create rhythm:
- **Hero card**: \`<Card padding="xl" shadow="lg" radius="xl">\` with branded gradient background — anchors the eye
- **Stat tiles**: \`<Card padding="md" shadow="sm" radius="md">\` with surface bg — secondary
- **Inline rows / list items**: no card chrome at all, just \`<Stack gap="sm">\` with dividers — tertiary

The hero should literally have higher elevation than the supporting tiles. If everything has \`shadow="md"\`, nothing does.

### Iconography — emoji + Icon are visual weight on the cheap

Don't render text-only metrics. A weather widget without a sun/cloud, a stock card without an arrow, a status panel without a colored dot — all feel undersold. Pair every hero metric with an icon or emoji at large size:

\`\`\`tsx
<Row gap="md" align="center">
  <Text size="3xl">☀️</Text>
  <Stack gap="xs">
    <Text size="4xl" weight="bold">18°C</Text>
    <Text size="sm" tone="muted">Sunny · feels like 16°</Text>
  </Stack>
</Row>
\`\`\`

Use \`<Icon name="..." />\` (Lucide icon names in kebab-case) for line icons; emoji directly for status/weather/mood. Both are valid. For per-stat tiny accents, use a small icon next to the label.

### Spacing — generosity beats compactness

Hero sections should feel airy. Use \`padding="xl"\` (32px) on the main card, not \`padding="md"\`. Whitespace IS design. A cramped polished card looks worse than a roomy plain one.

### Concrete recipes

- **Hero metric card** (weather, stock, score): hero number at \`size="4xl"\` (the max), icon/emoji at \`size="3xl"\` next to it (use \`<Row gap="md">\`), supporting label at \`size="sm"\` muted, branded gradient bg, \`shadow="lg"\`, \`padding="xl"\`.
- **Stat grid** (3–6 quick metrics): \`<Grid columns={3} gap="md">\` of \`<Stat>\` — each \`<Stat label="…" value="…" delta="…" trend="…" />\` handles the label-on-top / value-below / trend-coloured-delta layout for you. Wrap each in a \`<Card padding="md" shadow="sm">\` if you want tile chrome.
- **List item** (forecast day, todo, message): no card per item, use \`<Stack gap="md">\` with each row as \`<Row gap="md">\` of icon + content + meta. Add \`<Divider>\` between rows.
- **Multi-step wizard** (survey, onboarding, checkout): \`<Stepper steps={STEPS} current={step} />\` at the top, the current step's fields below, Next/Back buttons that move \`step\`. Don't hand-roll a "2 of 4" indicator.
- **Key-value rows** (specs, order summary, flight details): \`<Stack gap="sm">\` of \`<Row justify="between">\` — label as \`<Text size="sm" tone="muted">\`, value as \`<Text size="sm" weight="medium">\`.
- **Section header**: \`<Heading level={2}>\` left-aligned, optional \`<Badge>\` to its right for count/status, optional muted caption below.
- **CTA section**: ONE primary button. Other actions as ghost/outline. Don't stack three primary buttons.

## Quality Checklist (verify before returning)

- [ ] Imports ONLY from: react, @ggui-ai/design, @ggui-ai/wire
- [ ] No raw HTML elements (\`<button>\`, \`<input>\`, \`<div>\` for layout) — uses primitives
- [ ] ZERO hardcoded hex colors — every color is a bare \`var(--ggui-color-*)\`, no literal fallback
- [ ] No raw pixel values for spacing — all via \`var(--ggui-spacing-*)\` tokens
- [ ] Primary palette used throughout (headers, borders, icons) — not just buttons
- [ ] Typed Props interface exported; request-specific data is a prop with default
- [ ] Every Input/TextArea/Select has a \`label\` prop (no separate \`<Text>\` label)
- [ ] Icon-only buttons have \`aria-label\`; no redundant \`role\`/\`aria-*\` on primitives
- [ ] Toggleable/selected/expanded state is ARIA-expressed (\`aria-checked\`/\`aria-pressed\`/\`aria-selected\`/\`aria-expanded\`) — never style-only
- [ ] Live/streaming regions wrapped in \`aria-live="polite"\`
- [ ] Headings nest — one \`level={1}\`, then \`level={2}\`/\`{3}\` — never skipped or inverted
- [ ] Wire hooks (\`useAction\`, \`useStream\`) imported from \`@ggui-ai/wire\` and consumed`;
